import type { FormikProps } from 'formik';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

import api from '../api';
import type { AddressValidationResponse, AddressValidationSuggestion } from '../api/addressValidation';
import type { ClientFormValues } from '../models/Forms';
import { AddressSuggestionModalContent } from './addressSuggestionModalContent';
import {
  clearAddressValidationFieldErrors,
  hasActionableAddressSuggestionDiff,
  mapFieldIssuesToFormErrors,
  mergeAddressSuggestion,
  withAddressValidationMeta,
} from './applyAddressValidation';

export type MessageApi = {
  warning: (content: string) => void;
};

export type ModalApi = {
  confirm: (config: {
    centered?: boolean;
    title: string;
    content: ReactNode;
    okText: string;
    cancelText: string;
    onOk: () => void;
    onCancel: () => void;
  }) => void;
};

export type RunClientAddressValidationDeps = {
  formikRef: { current: FormikProps<ClientFormValues> | null };
  tForms: TFunction;
  message: MessageApi;
  modal: ModalApi;
};

type SuggestModalCopy = {
  titleKey?: string;
  introKey?: 'clientForm.addressValidation.suggestIntro' | 'clientForm.addressValidation.reviewIntro';
};

export type RunClientAddressValidationOutcome = { type: 'invalid' } | { type: 'completed'; values: ClientFormValues };

export async function runClientAddressValidation(
  values: ClientFormValues,
  { formikRef, tForms, message, modal }: RunClientAddressValidationDeps,
): Promise<RunClientAddressValidationOutcome> {
  const formik = formikRef.current;
  if (formik) {
    clearAddressValidationFieldErrors(formik);
  }

  try {
    const response = await api.addressValidation.create({
      street: values.street,
      streetNumber: values.streetNumber,
      city: values.city,
      psc: values.psc,
      state: values.state,
    });

    if (!response.ok || !response.data) {
      return completeWithProviderError(values, tForms, message);
    }

    return await resolveClientAddressValidationResult(values, response.data, { formikRef, tForms, message, modal });
  } catch {
    return completeWithProviderError(values, tForms, message);
  }
}

function completeWithProviderError(
  values: ClientFormValues,
  tForms: TFunction,
  message: MessageApi,
): RunClientAddressValidationOutcome {
  message.warning(tForms('clientForm.addressValidation.providerErrorWarning'));
  return { type: 'completed', values: withAddressValidationMeta(values, 'unverified') };
}

function applyInvalidAddressValidationResult(
  validationResult: AddressValidationResponse,
  formik: FormikProps<ClientFormValues> | null,
  tForms: TFunction,
): RunClientAddressValidationOutcome {
  const fieldErrors = mapFieldIssuesToFormErrors(validationResult, tForms);
  if (formik) {
    for (const [field, errorMessage] of Object.entries(fieldErrors)) {
      formik.setFieldError(field, errorMessage);
    }
    void formik.setTouched(
      {
        ...formik.touched,
        psc: true,
        city: true,
        state: true,
      },
      false,
    );
  }
  return { type: 'invalid' };
}

async function resolveAcceptedOrRejectedSuggestion(
  values: ClientFormValues,
  suggestion: AddressValidationSuggestion,
  deps: Pick<RunClientAddressValidationDeps, 'tForms' | 'modal'>,
  modalCopy: SuggestModalCopy = {},
): Promise<RunClientAddressValidationOutcome> {
  const choice = await confirmAddressSuggestion(values, suggestion, { ...deps, ...modalCopy });
  if (choice === 'accept') {
    return {
      type: 'completed',
      values: withAddressValidationMeta(mergeAddressSuggestion(values, suggestion), 'validated'),
    };
  }
  return { type: 'completed', values: withAddressValidationMeta(values, 'user_override') };
}

async function resolveUnverifiedAddressValidationResult(
  values: ClientFormValues,
  validationResult: AddressValidationResponse,
  deps: RunClientAddressValidationDeps,
): Promise<RunClientAddressValidationOutcome> {
  const { tForms, message } = deps;
  const suggestion = validationResult.suggestion;

  if (suggestion && hasActionableAddressSuggestionDiff(suggestion, values)) {
    return resolveAcceptedOrRejectedSuggestion(values, suggestion, deps, {
      titleKey: 'clientForm.addressValidation.reviewTitle',
      introKey: 'clientForm.addressValidation.reviewIntro',
    });
  }

  message.warning(tForms('clientForm.addressValidation.unverifiedWarning'));
  return { type: 'completed', values: withAddressValidationMeta(values, 'unverified') };
}

async function resolveClientAddressValidationResult(
  values: ClientFormValues,
  validationResult: AddressValidationResponse,
  deps: RunClientAddressValidationDeps,
): Promise<RunClientAddressValidationOutcome> {
  const { formikRef, tForms, message, modal } = deps;
  const formik = formikRef.current;

  if (validationResult.status === 'invalid') {
    return applyInvalidAddressValidationResult(validationResult, formik, tForms);
  }

  if (validationResult.status === 'suggest') {
    if (!validationResult.suggestion || !hasActionableAddressSuggestionDiff(validationResult.suggestion, values)) {
      message.warning(tForms('clientForm.addressValidation.unverifiedWarning'));
      return { type: 'completed', values: withAddressValidationMeta(values, 'unverified') };
    }
    return resolveAcceptedOrRejectedSuggestion(values, validationResult.suggestion, { tForms, modal });
  }

  if (validationResult.status === 'PROVIDER_ERROR') {
    return completeWithProviderError(values, tForms, message);
  }

  if (validationResult.status === 'unverified') {
    return resolveUnverifiedAddressValidationResult(values, validationResult, deps);
  }

  return { type: 'completed', values: withAddressValidationMeta(values, 'validated') };
}

function confirmAddressSuggestion(
  values: ClientFormValues,
  suggestion: AddressValidationSuggestion,
  {
    tForms,
    modal,
    titleKey = 'clientForm.addressValidation.suggestTitle',
    introKey = 'clientForm.addressValidation.suggestIntro',
  }: Pick<RunClientAddressValidationDeps, 'tForms' | 'modal'> & SuggestModalCopy,
): Promise<'accept' | 'reject'> {
  return new Promise((resolve) => {
    const openConfirm = () => {
      modal.confirm({
        centered: true,
        title: tForms(titleKey),
        content: (
          <AddressSuggestionModalContent suggestion={suggestion} values={values} t={tForms} introKey={introKey} />
        ),
        okText: tForms('clientForm.addressValidation.suggestAccept'),
        cancelText: tForms('clientForm.addressValidation.suggestReject'),
        onOk: () => {
          resolve('accept');
        },
        onCancel: () => {
          resolve('reject');
        },
      });
    };

    queueMicrotask(openConfirm);
  });
}
